// src/studio/pages/StudioProjectLayout.jsx — /studio/:projectId/*
//
// The shared shell for every stage page: the project header and the
// StageRail (plan D-1). Resolves which document backs the "design" stage
// from `applicableDocuments` (SPEC-07 D-2) so stage pages never hardcode
// that choice, and fetches each stage's latest run status once here rather
// than duplicating that fetch in every stage page.
import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { PageHeader, SkeletonText, Alert } from "../../shared/ui";
import StageRail from "../components/StageRail";
import { studioDocumentsApi, studioProjectsApi } from "../api/studioApi";

const STAGE_LABELS = {
  sources: "Sources",
  requirements: "Requirements",
  design: "Design",
  technical: "Technical Design",
};

export default function StudioProjectLayout() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [applicableDocuments, setApplicableDocuments] = useState([]);
  const [stageStatus, setStageStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [projectData, applicable] = await Promise.all([
        studioProjectsApi.get(projectId),
        studioDocumentsApi.applicable(projectId),
      ]);
      setProject(projectData);
      setApplicableDocuments(applicable);

      // Best-effort — a stage that hasn't been generated yet is a normal
      // "not started" state, not an error the page should surface.
      const runs = await Promise.all(
        applicable.map((doc) =>
          studioDocumentsApi
            .latestRun(projectId, doc.document_type)
            .catch(() => null)
        )
      );
      const next = {};
      applicable.forEach((doc, i) => { next[doc.stage] = runs[i]?.status || "not_started"; });
      setStageStatus(next);
    } catch {
      setError("Failed to load this project.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const designDocument = applicableDocuments.find((d) => d.stage === "design");

  // Managed Services engagements don't produce a Design or Technical Design
  // document in this pipeline — UI-only for now: the routes/API stay reachable
  // if linked to directly, only the stage-rail tabs are hidden.
  const isManagedServices =
    (project?.contract_type || "").trim().toLowerCase() === "managed services";

  const stages = [
    { key: "sources", label: STAGE_LABELS.sources, to: `/studio/${projectId}/sources`, status: undefined },
    {
      key: "requirements",
      label: STAGE_LABELS.requirements,
      to: `/studio/${projectId}/requirements`,
      status: stageStatus.requirements,
    },
    !isManagedServices && {
      key: "design",
      label: designDocument?.document_type === "sfdc_solution_design"
        ? "Solution Design"
        : STAGE_LABELS.design,
      to: `/studio/${projectId}/design`,
      status: stageStatus.design,
    },
    !isManagedServices && {
      key: "technical",
      label: STAGE_LABELS.technical,
      to: `/studio/${projectId}/technical`,
      status: stageStatus.technical,
    },
    // RAID and Traceability belong in this pipeline (SOW+Notes -> Requirements,
    // RAID, HLD, LLD, Traceability, Project Plan) but Compliance was built as
    // its own module before the studio existed, and hasn't been folded in yet.
    // This is a stopgap link out to it, not a real stage -- it carries no
    // status, and leaves /studio entirely rather than nesting under it.
    {
      key: "compliance",
      label: "Compliance & Audit",
      to: "/compliance",
      status: undefined,
    },
  ].filter(Boolean);

  if (loading) {
    return <SkeletonText lines={4} className="max-w-lg" />;
  }

  if (error || !project) {
    return (
      <Alert variant="danger" icon={AlertCircle} title="Couldn't load this project">
        {error || "It may have been deleted."}
      </Alert>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate("/studio")}
        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 mb-4 transition-colors"
      >
        <ArrowLeft size={14} /> Back to projects
      </button>

      <PageHeader
        title={project.name}
        subtitle={project.client_name || undefined}
      />

      <StageRail stages={stages} />

      <Outlet
        context={{
          project,
          applicableDocuments,
          designDocumentType: designDocument?.document_type || "high_level_design",
          reloadProject: load,
        }}
      />
    </div>
  );
}
